import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { LoadingBlock } from "@/components/LoadingState";
import { toast } from "sonner";
import { CalendarDays, MapPin, Play, ArrowLeft, Users, ListOrdered, CircleCheckBig, CircleDashed, AlertTriangle } from "lucide-react";
import { useOfficialMatch, useCreateFromOfficial, useMatches } from "@/queries/matches";
import { useRefreshMatchLineup } from "@/queries/matches";
import { useRestartMatchLineup } from "@/queries/matches";
import { getTeamRiders } from "@/api/teams";
import { useAuth } from "@/contexts/AuthContext";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { asArray } from "@/lib/asArray";
import { cn } from "@/lib/utils";
import { formatSvDate, formatSvTime } from "@/utils/date";
import PageTutorial from "@/components/tutorial/PageTutorial";
import TeamLogo from "@/components/TeamLogo";
import GuestRiderBadge from "@/components/GuestRiderBadge";
import HeatProgramMatrix from "@/components/protocol/HeatProgramMatrix";
import Seo from "@/components/Seo";
import { isGuestRiderForTeam } from "@/lib/guestRiders";
import { getNativePlatform } from "@/lib/runtime";
import { NativeOfficialMatchSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

function formatDate(value) {
  if (!value) return null;
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return {
      dateLabel: formatSvDate(dt, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      timeLabel: formatSvTime(dt, {
        hour: "2-digit",
        minute: "2-digit",
      }),
      date: dt,
    };
  } catch {
    return null;
  }
}

function renderLineup(entries = []) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  return entries
    .slice()
    .sort((a, b) => (a?.lineup_no ?? 0) - (b?.lineup_no ?? 0))
    .map((entry) => {
      const num = entry?.lineup_no ?? "?";
      const name = entry?.name || entry?.rider_name || entry?.rider || entry?.rider_id || "Okänd";
      return (
        <li key={`${num}-${name}`} className="flex gap-2">
          <span className="w-6 text-muted-foreground">#{num}</span>
          <span className="flex min-w-0 items-center gap-2">
            <span>{name}</span>
            {isGuestRiderForTeam(entry) ? <GuestRiderBadge className="shrink-0" /> : null}
          </span>
        </li>
      );
    });
}

function isManualCancelled(match) {
  return String(match?.manual_status || "").trim().toLowerCase() === "cancelled";
}

export default function OfficialMatchDetailContent({
  officialId,
  embedded = false,
  onBack = null,
  onStartProtocol = null,
  showTutorial = true,
}) {
  const { user } = useAuth();
  const { requestProtocolSession } = useProtocolShell();
  const isNativeAndroid = getNativePlatform() === "android";

  const { data, isLoading, error } = useOfficialMatch(officialId);
  const [homeRiders, setHomeRiders] = useState(null);
  const [awayRiders, setAwayRiders] = useState(null);
  const [resumeDecision, setResumeDecision] = useState(null);
  const createMutation = useCreateFromOfficial();
  const refreshMutation = useRefreshMatchLineup();
  const restartMutation = useRestartMatchLineup();

  const { data: userMatches = [] } = useMatches({
    enabled: !!user,
    staleTime: Infinity,
  });

  const startInfo = useMemo(() => {
    const raw = data?.date || data?.start || data?.start_time;
    return formatDate(raw);
  }, [data?.date, data?.start, data?.start_time]);

  const consumedByUser = useMemo(() => {
    if (data?.consumedByCurrentUser) return true;
    const officialIdStr = String(data?.id ?? data?._id ?? "");
    if (!officialIdStr) return false;
    const list = asArray(userMatches);
    return list.some((match) => {
      const fromMatch =
        match?.official_match_id ??
        match?.officialMatchId ??
        match?.official_snapshot?.id ??
        match?.officialSnapshot?.id;
      if (!fromMatch) return false;
      return String(fromMatch) === officialIdStr;
    });
  }, [data?.consumedByCurrentUser, data?.id, data?._id, userMatches]);

  const hasLineups = Boolean(data?.lineups?.home?.length && data?.lineups?.away?.length);
  const isCancelled = isManualCancelled(data);

  useEffect(() => {
    if (!data?.home_team_id || !data?.away_team_id) return;
    const season = data?.season || (data?.date ? new Date(data.date).getFullYear() : undefined);
    getTeamRiders(data.home_team_id, {
      season,
      active: true,
      includeGuests: true,
      officialMatchId: data.id,
    })
      .then((res) => setHomeRiders(res))
      .catch(() => setHomeRiders(null));
    getTeamRiders(data.away_team_id, {
      season,
      active: true,
      includeGuests: true,
      officialMatchId: data.id,
    })
      .then((res) => setAwayRiders(res))
      .catch(() => setAwayRiders(null));
  }, [data?.home_team_id, data?.away_team_id, data?.season, data?.date]);

  const handleStart = async () => {
    if (!data?.id) return;
    if (isCancelled) {
      toast.error("Matchen är markerad som inställd.");
      return;
    }
    if (!hasLineups) {
      toast.error("Laguppställning saknas. Kontakta admin.");
      return;
    }

    const promise = createMutation.mutateAsync(data.id);
    toast.promise(promise, {
      error: (e) => e?.message || "Kunde inte starta matchen",
    });

    try {
      const result = await promise;
      const matchId = result?.match_id ?? result?.id ?? result?.matchId;
      const lineupSync = result?.lineup_sync || null;
      if (!matchId) {
        toast.error("Protokollet skapades men kunde inte öppnas automatiskt.");
        return;
      }
      if (result?.reused && lineupSync?.status === "outdated") {
        setResumeDecision({
          matchId: String(matchId),
          lineupSync,
          title,
          startLabel: [startInfo?.dateLabel, startInfo?.timeLabel].filter(Boolean).join(" • "),
        });
        return;
      }
      if (typeof onStartProtocol === "function") {
        onStartProtocol(String(matchId));
        return;
      }
      requestProtocolSession(matchId);
    } catch {
      return;
    }
  };

  const openProtocol = (matchId) => {
    if (typeof onStartProtocol === "function") {
      onStartProtocol(String(matchId));
      return;
    }
    requestProtocolSession(matchId);
  };

  const handleRefreshAndOpen = async () => {
    const matchId = resumeDecision?.matchId;
    if (!matchId) return;
    const promise = refreshMutation.mutateAsync(matchId);
    toast.promise(promise, {
      loading: "Uppdaterar laguppställning...",
      success: "Protokollet uppdaterades till senaste lineup.",
      error: (e) => e?.message || "Kunde inte uppdatera protokollet.",
    });
    try {
      await promise;
      setResumeDecision(null);
      openProtocol(matchId);
    } catch {
      return;
    }
  };

  const handleContinueWithOutdated = () => {
    const matchId = resumeDecision?.matchId;
    if (!matchId) return;
    setResumeDecision(null);
    openProtocol(matchId);
  };

  const handleRestartAndOpen = async () => {
    const matchId = resumeDecision?.matchId;
    if (!matchId) return;
    const promise = restartMutation.mutateAsync(matchId);
    toast.promise(promise, {
      loading: "Börjar om protokollet...",
      success: "Protokollet återställdes till senaste lineup.",
      error: (e) => e?.message || "Kunde inte börja om protokollet.",
    });
    try {
      await promise;
      setResumeDecision(null);
      openProtocol(matchId);
    } catch {
      return;
    }
  };

  const homeTeamName = data?.home_team_display || data?.home_team_name_canonical || data?.home_team || data?.home || "Hemmalag";
  const awayTeamName = data?.away_team_display || data?.away_team_name_canonical || data?.away_team || data?.away || "Bortalag";
  const protocolAvailabilityLabel = hasLineups ? "Tillgänglig" : "Ej tillgänglig";
  const ProtocolStatusIcon = hasLineups ? CircleCheckBig : CircleDashed;
  const title = `${homeTeamName} vs ${awayTeamName}`;
  const tutorialReady = !isLoading && !error && !!data;
  const seoDescription = startInfo
    ? `${homeTeamName} mot ${awayTeamName}. Se matchkort, körschema, laguppställning och matchinformation i Lapstr.`
    : `${homeTeamName} mot ${awayTeamName}. Se matchkort, körschema och laguppställning i Lapstr.`;

  return (
    <div className={cn("space-y-6", isNativeAndroid && "native-v3-official-match")}>
      <Seo
        title={title}
        description={seoDescription}
        canonicalPath={officialId ? `/matches/official/${officialId}` : "/matches"}
      />
      {showTutorial ? (
        <PageTutorial pageName="officialMatchDetail" autoStart={tutorialReady} />
      ) : null}

      {!embedded ? (
        <div className="hidden sm:block">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/matches">Matcher</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      ) : null}

      <div className="native-v3-official-actions flex flex-wrap items-center justify-start gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {embedded ? (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Tillbaka
            </Button>
          ) : (
            <Button variant="ghost" size="sm" asChild>
              <Link to="/matches" className="flex items-center gap-1">
                <ArrowLeft className="h-4 w-4" /> Tillbaka
              </Link>
            </Button>
          )}
          <Button
            id="startProtocolButton"
            size="sm"
            onClick={handleStart}
            disabled={createMutation.isPending || !hasLineups || isCancelled}
            className="flex items-center gap-1"
          >
            <Play className="h-4 w-4" /> {consumedByUser ? "Öppna protokoll" : "Starta protokoll"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        isNativeAndroid ? <NativeOfficialMatchSkeleton /> : <LoadingBlock text="Hämtar match" />
      ) : error ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-red-600">
            Kunde inte hämta matchinformationen.
          </CardContent>
        </Card>
      ) : !data ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Matchen saknas eller har tagits bort.
          </CardContent>
        </Card>
      ) : (
        <div className="grid min-w-0 gap-6 lg:grid-cols-[2fr,3fr]">
          <Card id="matchInfoCard" className="native-v3-official-hero min-w-0">
            <CardHeader>
              <CardTitle>Matchinformation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {isCancelled ? (
                <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-semibold">Matchen är inställd</p>
                    <p className="text-sm text-amber-800/90 dark:text-amber-200/90">
                      Protokollstart är inaktiverad för denna match.
                    </p>
                  </div>
                </div>
              ) : null}
              <div className="native-v3-official-teams grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-xl border border-border/60 bg-secondary/30 px-4 py-3 text-center">
                <div className="min-w-0">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center">
                    <TeamLogo teamName={homeTeamName} className="h-10 w-10 object-contain" />
                  </div>
                  <p className="truncate text-sm font-semibold text-foreground">{homeTeamName}</p>
                </div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  vs
                </div>
                <div className="min-w-0">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center">
                    <TeamLogo teamName={awayTeamName} className="h-10 w-10 object-contain" />
                  </div>
                  <p className="truncate text-sm font-semibold text-foreground">{awayTeamName}</p>
                </div>
              </div>
              <div className="native-v3-official-meta grid gap-3">
                <div className="flex items-center gap-3">
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {startInfo?.dateLabel ?? "TBA"}
                    {startInfo?.timeLabel ? ` • ${startInfo.timeLabel}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <p className="text-muted-foreground">{data?.venue || data?.location || "Ej angivet"}</p>
                </div>
                <div className="flex items-start gap-3">
                  <ProtocolStatusIcon className="mt-1 h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Protokollstatus</p>
                    <Badge
                      className="native-v3-status-badge"
                      variant={isCancelled ? "destructive" : hasLineups ? "outline" : "secondary"}
                    >
                      {isCancelled ? "Inställd" : protocolAvailabilityLabel}
                    </Badge>
                  </div>
                </div>
              </div>

            </CardContent>
          </Card>

          <Card id="heatTable" className="native-v3-official-program min-w-0 overflow-hidden lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ListOrdered className="h-5 w-5 shrink-0 opacity-70" /><span>Körschema och laguppställning</span></CardTitle>
              <CardDescription>Förhandsvy av matchens körschema och laguppställning.</CardDescription>
            </CardHeader>
            <CardContent className="min-w-0">
              <HeatProgramMatrix
                heats={data?.heats}
                lineups={data?.lineups}
                homeTeamName={homeTeamName}
                awayTeamName={awayTeamName}
                emptyText="Körschema och laguppställning ej färdigställt."
              />
            </CardContent>
          </Card>
        </div>
      )}

      <AlertDialog
        open={Boolean(resumeDecision)}
        onOpenChange={(open) => {
          if (!open) setResumeDecision(null);
        }}
      >
        <AlertDialogContent className={isNativeAndroid ? "native-v3-protocol-alert" : undefined}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resumeDecision?.lineupSync?.can_refresh
                ? "Laguppställningen har ändrats"
                : "Protokollet bygger på äldre laguppställning"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="mb-2 block font-medium text-foreground">
                {resumeDecision?.title}
                {resumeDecision?.startLabel ? ` • ${resumeDecision.startLabel}` : ""}
              </span>
              {resumeDecision?.lineupSync?.can_refresh
                ? "Den officiella laguppställningen har ändrats sedan du skapade protokollet. Eftersom inga heat har körts ännu kan protokollet uppdateras till senaste version."
                : "Den officiella laguppställningen har ändrats sedan du skapade protokollet. Du kan fortsätta med nuvarande protokoll eller börja om med senaste lineup."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="native-v3-protocol-alert-footer">
            <AlertDialogCancel className="native-v3-protocol-alert-cancel" disabled={refreshMutation.isPending || restartMutation.isPending}>Avbryt</AlertDialogCancel>
            {resumeDecision?.lineupSync?.can_refresh ? (
              <AlertDialogAction className="native-v3-protocol-alert-primary" onClick={handleRefreshAndOpen} disabled={refreshMutation.isPending}>
                {refreshMutation.isPending ? "Uppdaterar..." : "Uppdatera protokoll"}
              </AlertDialogAction>
            ) : (
              <>
                <Button className="native-v3-protocol-alert-secondary" variant="outline" onClick={handleContinueWithOutdated} disabled={restartMutation.isPending}>
                  Fortsätt ändå
                </Button>
                <AlertDialogAction className="native-v3-protocol-alert-primary" onClick={handleRestartAndOpen} disabled={restartMutation.isPending}>
                  {restartMutation.isPending ? "Börjar om..." : "Börja om protokoll"}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}



